import { computed, ref } from 'vue';
import {
  STATES,
  usePersonalSurveyProfile,
  useTeamFeaturedArticles,
  useTeamInfo,
  useThemeList,
  useUserInfo,
} from '@/utils/swr';
import { useStore } from '@/utils/vue';

export const useInsightsAvailability = () => {
  const store = useStore();
  const { data: teamInfo, state: teamState } = useTeamInfo();
  const { data: userInfo, state: userState } = useUserInfo();
  const feedType = computed(() => store.state.feedType.feedType);
  const { data: themes, state: themesState } = useThemeList(feedType);

  const userProfileEnabled = computed(
    () => teamInfo.value?.enable_profile ?? false,
  );

  const {
    data: personalSurveyProfile,
    state: personalSurveyProfileState,
    mutate: mutateProfile,
  } = usePersonalSurveyProfile(userProfileEnabled);

  const hasProfileLoaded = computed(
    () => personalSurveyProfileState.value === STATES.SUCCESS,
  );
  const hasLoaded = computed(
    () =>
      teamState.value === STATES.SUCCESS &&
      userState.value === STATES.SUCCESS &&
      hasProfileLoaded.value &&
      themesState.value === STATES.SUCCESS,
  );

  const { data: featuredArticles } = useTeamFeaturedArticles(
    ref('mark'),
    ref(1),
  );

  const hasFavoriteThemes = computed(() =>
    themes.value?.themes.some(
      t => !t.is_deleted && t.is_member && t.is_favorite,
    ),
  );
  const hasSetupPersonalNewsOrFavoriteThemes = computed(
    () =>
      !!userInfo.value?.sent_first_personal_news_feed_at ||
      !!hasFavoriteThemes.value,
  );
  const hasPersonalNewsArticles = computed(
    () => userInfo.value?.has_personal_news ?? false,
  );
  const hasProfile = computed(
    () =>
      personalSurveyProfile.value !== undefined &&
      (personalSurveyProfile.value.department.length > 0 ||
        personalSurveyProfile.value.market_area.length > 0 ||
        personalSurveyProfile.value.technical_challenge.length > 0 ||
        personalSurveyProfile.value.technology_area.length > 0),
  );
  const hasFeaturedArticles = computed(
    () => (featuredArticles.value?.featured_articles ?? []).length > 0,
  );

  const isInsightEnabled = computed(
    () => userProfileEnabled.value && hasProfile.value,
  );

  return {
    hasLoaded,
    hasProfileLoaded,
    userProfileEnabled,
    isInsightEnabled,
    hasProfile,
    hasSetupPersonalNewsOrFavoriteThemes,
    hasPersonalNewsArticles,
    hasFeaturedArticles,
    personalSurveyProfile,
    mutateProfile,
  };
};
